/**
 * Customer contacts keyed by contract number (`contacts_chunks`).
 *
 * The rental system's contract lists carry no phone; only its "Contracts
 * Report" does. The fines engine pulls that report from Ejar every hour and
 * stores it here as 4000-row chunks - the E-Check archive pattern - loaded once
 * per day into memory (IndexedDB cache), so every page that shows a contract
 * can look the phone up without a Firestore read. It is no longer uploaded by
 * hand (owner, 28 Sep 2026: every list comes from Ejar); this file only reads.
 *
 * Row shape (short keys, ~45k rows): { c: contract no (upper), p: phone digits
 * with country code, n: customer name, i: identity number, b: pick-up branch,
 * d: pick-up date text, e: drop-off date text, s: status, t: type }.
 */
import { db } from '../firebase';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { jsonCacheService } from './jsonCacheService';
import logger from '../utils/logger';
import { phoneDigits, formatPhone, whatsappUrl } from '../utils/phoneUtils';
import { USE_CONTRACTS_V2, onStoreRecords, contactRowOfRecord, v2OpenRecords, v2RecordsByIds, v2SearchRecords, v2CustomerRecords } from './contractsV2';
import { passportKey } from './passportKey';

export const CONTACTS_COLLECTION = 'contacts_chunks';
const METADATA_DOC = '_metadata';
const CACHE_FILE = 'contacts.json';
const CACHE_HOURS = 2;   // the engine merges new phones/statuses hourly; refresh the browser copy every ~2h

/** A contacts row's status `s` is open only when it is Ejar's "Open": an empty / unknown status
 *  (the row kept the status it had when first seen) is NOT open (26 Sep 2026). */
export const isOpenStatus = (s) => String(s || '').trim().toLowerCase() === 'open';
/** Contract numbers are compared upper-cased and trimmed (the contract doc ids). */
export const contractKey = (value) => (value === null || value === undefined ? '' : String(value).trim().toUpperCase());

// The one phone rule (keeps the country code) lives in utils/phoneUtils; re-exported for the pages that import it here.
export { phoneDigits, formatPhone, whatsappUrl };

let memoryChunks = null;      // [{ id, data }]
let memoryIndex = null;       // Map contract -> row
let memoryPhoneIndex = null;  // Map last-9-digits -> [rows], newest pick-up first (phone -> contract lookup)
let memoryIdentityIndex = null; // Map identity -> [rows], newest pick-up first (Customer profile)
let memoryCustomerIndex = null; // Map Ejar customer number -> [rows], newest pick-up first (THE customer)
let loading = null;

const buildIndex = (chunks) => {
    const index = new Map();
    for (const ch of chunks) for (const row of ch.data || []) if (row && row.c && !index.has(row.c)) index.set(row.c, row);
    return index;
};

/** dd/mm/yyyy[ HH:MM] -> a sortable number (0 when unparseable), for "newest contract first". */
const dateSortKey = (d) => {
    const m = /^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2}))?/.exec(String(d || ''));
    return m ? Number(`${m[3]}${m[2]}${m[1]}${m[4] || '00'}${m[5] || '00'}`) : 0;
};

// --- Names: a typo still finds the customer ("Ahmed Elamir" -> "Ahmed Alamir Ali Ebrahim") ---
/** Lower case, one space, Arabic letter variants folded (أ إ آ -> ا, ة -> ه, ى -> ي, no tashkeel). */
export const nameKey = (v) => String(v || '').toLowerCase()
    .replace(/[\u064B-\u0652\u0640]/g, '')
    .replace(/[أإآ]/g, 'ا').replace(/ة/g, 'ه').replace(/ى/g, 'ي')
    .replace(/\s+/g, ' ').trim();

// Levenshtein distance with an early exit once it is above `max`
const editDistance = (a, b, max) => {
    if (Math.abs(a.length - b.length) > max) return max + 1;
    let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
    for (let i = 1; i <= a.length; i += 1) {
        const cur = [i];
        let best = i;
        for (let j = 1; j <= b.length; j += 1) {
            cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
            if (cur[j] < best) best = cur[j];
        }
        if (best > max) return max + 1;
        prev = cur;
    }
    return prev[b.length];
};
// how many wrong letters a typed word may have: none under 4 letters, 1 up to 7, 2 from 8
const slackOf = (word) => (word.length >= 8 ? 2 : word.length >= 4 ? 1 : 0);

/**
 * How far typed words are from a stored name: 0 = every word found as typed, a positive
 * number = that many letters off in total, null = some word matches nothing. A typed word
 * matches a name word that contains it, or one (or its start) within its slack.
 */
export const nameDistance = (tokens, name) => {
    const words = nameKey(name).split(' ').filter(Boolean);
    let total = 0;
    for (const t of tokens) {
        let best = null;
        for (const w of words) {
            if (w.includes(t)) { best = 0; break; }
            const slack = slackOf(t);
            if (!slack) continue;
            const d = Math.min(editDistance(t, w, slack), editDistance(t, w.slice(0, t.length), slack));
            if (d <= slack && (best === null || d < best)) best = d;
        }
        if (best === null) return null;
        total += best;
    }
    return total;
};

// Phones are matched on the last 9 digits so a number stored with a country code
// (966.., 971..) is found whether the user types the code or not.
const phoneKey = (digits) => { const d = String(digits || '').replace(/\D/g, ''); return d.length >= 9 ? d.slice(-9) : d; };

const buildPhoneIndex = (chunks) => {
    const index = new Map();
    for (const ch of chunks) for (const row of ch.data || []) {
        if (!row || !row.p || !row.c) continue;
        const key = phoneKey(row.p);
        if (key.length < 7) continue;
        (index.get(key) || index.set(key, []).get(key)).push(row);
    }
    for (const list of index.values()) list.sort((a, b) => dateSortKey(b.d) - dateSortKey(a.d));
    return index;
};

/** Identity (Emirates ID / passport) normalised: strip spaces/punctuation, upper. */
export const identityKey = (value) => String(value === null || value === undefined ? '' : value).replace(/[^A-Za-z0-9]/g, '').toUpperCase();

/** A passport typed in a search (N1234567, AB1234567 ...): the identity to look up, else '' (./passportKey). */
export { passportKey };

// --- THE customer: one rule for Ask Yelo's list and the Customer profile (owner, 28 Sep 2026) ---
// Ask Yelo grouped by identity, else phone, else name ("Ali · 5 contracts") while the profile took the identity only
// and opened 1 contract. Now both take customerKeyOf: the IDENTITY first (a customer with an ID is never merged or
// split by phone - the number can change); a contract with no ID on file is linked to the other no-ID contracts of
// its phone (the profile says "linked by phone"); with neither it stands alone ("current rental only"). Every hit
// stands for its whole customer (customerRowsOf), so the count Ask Yelo shows is exactly what the profile opens.
// The no-ID link is the WHOLE phone with its country code (phoneDigits - the app's one phone rule): the last 9 digits
// made a UAE 9715XXXXXXXX and a Saudi 9665XXXXXXXX one customer (review, owner 28 Sep 2026). phoneKey (last 9) stays
// for SEARCH only, where staff may type a number without its code.
// Ejar's customer number comes FIRST (owner, 4 Oct 2026: one customer showed a passport on his 2025 contract and a Saudi
// ID on the 2026 one - two customers here, one on Ejar, customer 7656013). A row without the number yet (its Ejar list
// row not read) takes the number of another contract of its identity, so it never stands as a customer of its own.
const noIdPhoneOf = (raw) => { const d = phoneDigits(raw); return d.length >= 7 ? d : ''; };
/** Ejar's customer number as the rows keep it (digits), else ''. */
export const ejarCustomerKey = (value) => { const d = String(value ?? '').trim(); return /^\d{1,18}$/.test(d) ? d : ''; };
const customerOfIdentity = (id) => (id.length >= 4 && memoryIdentityIndex
    ? ((memoryIdentityIndex.get(id) || []).map((r) => ejarCustomerKey(r.u)).find(Boolean) || '') : '');
export const customerKeyOf = (row) => {
    const id = identityKey(row && row.i);
    const u = ejarCustomerKey(row && row.u) || customerOfIdentity(id);
    if (u) return `u:${u}`;
    if (id.length >= 4) return `i:${id}`;
    const phone = noIdPhoneOf(row && row.p);
    if (phone) return `p:${phone}`;
    return `c:${contractKey(row && row.c)}`;
};

const onePerContract = (rows) => {
    const seen = new Set();
    return (rows || []).filter((r) => r && r.c && !seen.has(r.c) && seen.add(r.c));
};

/** Every contract row in memory of the customer `key` (customerKeyOf), newest pick-up first; a new array. */
const customerRowsOf = (key) => {
    const k = String(key || '');
    const v = k.slice(2);
    if (k.startsWith('u:')) {
        // the customer's contracts, and those of his identities whose Ejar list row was not read yet (no number)
        const own = (memoryCustomerIndex && memoryCustomerIndex.get(v)) || [];
        const ids = [...new Set(own.map((r) => identityKey(r.i)).filter((x) => x.length >= 4))];
        const more = ids.flatMap((x) => ((memoryIdentityIndex && memoryIdentityIndex.get(x)) || []).filter((r) => !ejarCustomerKey(r.u)));
        return onePerContract([...own, ...more]).sort((a, b) => dateSortKey(b.d) - dateSortKey(a.d));
    }
    if (k.startsWith('i:')) return onePerContract(memoryIdentityIndex && memoryIdentityIndex.get(v));
    // the phone index is keyed by the last 9 digits (search): only the no-ID rows of exactly this phone, code and all
    if (k.startsWith('p:')) {
        return onePerContract(((memoryPhoneIndex && memoryPhoneIndex.get(phoneKey(v))) || [])
            .filter((r) => identityKey(r.i).length < 4 && !ejarCustomerKey(r.u) && noIdPhoneOf(r.p) === v));
    }
    const row = k.startsWith('c:') && memoryIndex ? memoryIndex.get(v) : null;
    return row ? [row] : [];
};

const buildIdentityIndex = (chunks) => {
    const index = new Map();
    for (const ch of chunks) for (const row of ch.data || []) {
        if (!row || !row.c) continue;
        const key = identityKey(row.i);
        if (key.length < 4) continue;   // skip blanks / junk identities
        (index.get(key) || index.set(key, []).get(key)).push(row);
    }
    for (const list of index.values()) list.sort((a, b) => dateSortKey(b.d) - dateSortKey(a.d));
    return index;
};

const buildCustomerIndex = (chunks) => {
    const index = new Map();
    for (const ch of chunks) for (const row of ch.data || []) {
        const key = row && row.c ? ejarCustomerKey(row.u) : '';
        if (key) (index.get(key) || index.set(key, []).get(key)).push(row);
    }
    for (const list of index.values()) list.sort((a, b) => dateSortKey(b.d) - dateSortKey(a.d));
    return index;
};

const remember = (chunks) => {
    memoryChunks = chunks;
    memoryIndex = buildIndex(chunks);
    memoryPhoneIndex = buildPhoneIndex(chunks);
    memoryIdentityIndex = buildIdentityIndex(chunks);
    memoryCustomerIndex = buildCustomerIndex(chunks);
};

// --- The contracts store as the source (USE_CONTRACTS_V2, 27 Sep 2026: "we will not rely on Firebase anymore") ---
// No 45k-row copy in the browser: memory holds the OPEN contracts of every branch (one read, again after 10 min),
// every contract a page has read from the store (contractsV2 tells each read), and what a search or a lookup by
// number asked for (prefetch / searchStore / prepareCustomer). peek / findCustomers / customerContracts then answer
// from memory as before.
const STORE_OPEN_KEEP_MS = 10 * 60 * 1000;
const storeRows = new Map();          // contract -> contacts row, the store's record as read last
let storeOpenAt = 0;                   // when the open list was read (0 = not yet / cleared)
const storeFresh = () => storeOpenAt > 0 && Date.now() - storeOpenAt < STORE_OPEN_KEEP_MS;
const addStoreRows = (rows) => {
    let added = 0;
    for (const row of rows || []) if (row && row.c) { storeRows.set(row.c, row); added += 1; }
    if (added || !memoryIndex) remember([{ id: 'store', data: [...storeRows.values()] }]);
};
const addStoreRecords = (records) => addStoreRows((records || []).map(contactRowOfRecord));
if (USE_CONTRACTS_V2) onStoreRecords(addStoreRecords);

// A contract a page shows that memory lacks - a CLOSED one (owner, 16 Oct 2026: "فى قائمة المخالفات ليه فى عملاء كثير
// ملهومش ارقام تليفونات"; the store has the phone of every contract): the numbers asked within WANT_GATHER_MS are read
// together (one store read for a page of phone cells), each once a session; the askers are told when they are in.
const WANT_GATHER_MS = 30;
let wantQueue = new Set();
let wantTimer = null;
const wantAsked = new Set();
const wantListeners = new Set();

export const contactsService = {
    /** Chunks of the store ([{ id, data }]): memory, then the 24h IndexedDB cache, then Firestore. With
     *  USE_CONTRACTS_V2: the open contracts of every branch from the contracts store (see storeRows). */
    async load({ forceRefresh = false } = {}) {
        if (USE_CONTRACTS_V2) return this._loadStore({ forceRefresh });
        return this._loadChunks({ forceRefresh });
    },

    async _loadStore({ forceRefresh = false } = {}) {
        if (storeFresh() && memoryChunks && !forceRefresh) return memoryChunks;
        if (loading && !forceRefresh) return loading;
        loading = (async () => {
            try {
                addStoreRecords(await v2OpenRecords('all'));
                storeOpenAt = Date.now();
                return memoryChunks;
            } finally {
                loading = null;
            }
        })();
        return loading;
    },

    /** The Firestore contacts store (`contacts_chunks`), as the engine's hourly Ejar sync wrote it. */
    async _loadChunks({ forceRefresh = false } = {}) {
        if (memoryChunks && !forceRefresh) return memoryChunks;
        if (loading && !forceRefresh) return loading;
        loading = (async () => {
            try {
                if (!forceRefresh) {
                    const cached = await jsonCacheService.loadContacts();
                    const valid = await jsonCacheService.isCacheValid(CACHE_FILE, CACHE_HOURS);
                    if (Array.isArray(cached) && cached.length > 0 && valid) { remember(cached); return cached; }
                }
                const snap = await getDocs(query(collection(db, CONTACTS_COLLECTION), orderBy('__name__')));
                const chunks = [];
                snap.forEach(d => { if (d.id !== METADATA_DOC) { const data = d.data(); if (Array.isArray(data.data)) chunks.push({ id: d.id, data: data.data }); } });
                if (chunks.length > 0) await jsonCacheService.saveContacts(chunks);
                remember(chunks);
                return chunks;
            } finally {
                loading = null;
            }
        })();
        return loading;
    },

    /** Resolves once the store is in memory (errors are logged, not thrown: pages still render). */
    async ensureLoaded() {
        if (USE_CONTRACTS_V2 ? storeFresh() : memoryIndex) return true;
        try { await this.load(); return true; } catch (e) { logger.warn('[contacts] load failed:', e); return false; }
    },

    isLoaded: () => (USE_CONTRACTS_V2 ? storeOpenAt > 0 : !!memoryIndex),

    /**
     * These contracts in memory, so peek finds them: with USE_CONTRACTS_V2 the ones not there yet are read from the
     * store by number (any branch, open or closed); else the whole store is loaded. true when they could be read
     * (a failure is logged, never thrown: the page shows its own values).
     */
    async prefetch(contractNos) {
        if (!USE_CONTRACTS_V2) return this.ensureLoaded();
        const missing = [...new Set((contractNos || []).map(contractKey).filter((c) => c && !this.peek(c)))];
        if (!missing.length) return true;
        try { addStoreRecords(await v2RecordsByIds(missing, 'all')); return true; } catch (e) { logger.warn('[contacts] contracts not read from the store:', e); return false; }
    },

    /**
     * Makes findCustomers(term) see every customer the text can find: with USE_CONTRACTS_V2 the store's search for
     * it (name - also slightly misspelt -, phone with or without the country code, identity, contract, booking,
     * plate) is added to memory; else the whole store is loaded. false when it could not be searched.
     */
    async searchStore(term) {
        if (!USE_CONTRACTS_V2) return this.ensureLoaded();
        const q = String(term ?? '').trim();
        try {
            if (!storeFresh()) await this.load();
            if (q) addStoreRecords(await v2SearchRecords(q, 'all'));
        } catch (e) {
            logger.warn('[contacts] customer search failed:', e);
            return false;
        }
        // each customer found, whole: the store is read for its identity (or, with no ID, its phone) as the profile
        // reads it (prepareCustomer), so the count Ask Yelo shows is the count the profile opens (owner, 28 Sep 2026)
        if (q) await this._completeCustomers(this.findCustomers(q));
        return true;
    },

    /** The store's contracts of these customers (findCustomers groups) into memory; a failure is logged, never thrown. */
    async _completeCustomers(groups) {
        const customers = [...new Set((groups || []).map((g) => String((g && g.key) || ''))
            .filter((k) => k.startsWith('u:')).map((k) => k.slice(2)))];
        await Promise.all(customers.map((u) => v2CustomerRecords(u, 'all').then(addStoreRecords)
            .catch((e) => logger.warn('[contacts] customer contracts not read from the store:', e))));
        const texts = [...new Set((groups || []).map((g) => {
            const k = String((g && g.key) || '');
            // a phone is searched by its last 9 digits (the store finds it with or without its code); the group keeps
            // only its own full number (customerRowsOf)
            return k.startsWith('i:') ? k.slice(2) : k.startsWith('p:') ? phoneKey(k.slice(2)) : '';
        }).filter(Boolean))];
        await Promise.all(texts.map((t) => v2SearchRecords(t, 'all').then(addStoreRecords)
            .catch((e) => logger.warn('[contacts] customer contracts not read from the store:', e))));
        // an identity / phone group whose rows turned out to carry Ejar's customer number: that customer is read too,
        // so the count Ask Yelo shows is what the profile opens (prepareCustomer reads it the same way)
        const later = [...new Set((groups || []).flatMap((g) => (g && g.rows) || [])
            .map((r) => customerKeyOf(memoryIndex ? (memoryIndex.get(r.c) || r) : r))
            .filter((k) => k.startsWith('u:')).map((k) => k.slice(2)).filter((u) => !customers.includes(u)))];
        await Promise.all(later.map((u) => v2CustomerRecords(u, 'all').then(addStoreRecords)
            .catch((e) => logger.warn('[contacts] customer contracts not read from the store:', e))));
    },

    /**
     * Makes customerContracts({ identity, contractNo }) whole: with USE_CONTRACTS_V2 the contract itself and every
     * contract of its customer's identity are read from the store into memory; else the whole store is loaded.
     */
    async prepareCustomer({ identity, contractNo, phone } = {}) {
        if (!USE_CONTRACTS_V2) return this.ensureLoaded();
        try {
            if (contractNo) await this.prefetch([contractNo]);
            const seed = this.peek(contractNo) || {};
            const id = identityKey(identity) || identityKey(seed.i);
            if (id.length >= 4) addStoreRecords(await v2SearchRecords(id, 'all'));
            else if (!ejarCustomerKey(seed.u)) {
                // no ID on file: the contracts of its phone (customerKeyOf - the same rule as Ask Yelo's list)
                const ph = phoneKey(seed.p || (contractNo ? '' : phone));
                if (ph.length >= 7) addStoreRecords(await v2SearchRecords(ph, 'all'));
            }
            // then every contract of the Ejar customer (THE customer - customerKeyOf), whatever ID each one shows
            const u = ejarCustomerKey(seed.u) || customerOfIdentity(id);
            if (u) addStoreRecords(await v2CustomerRecords(u, 'all'));
            return true;
        } catch (e) {
            logger.warn('[contacts] customer contracts not read from the store:', e);
            return false;
        }
    },

    /**
     * Asks for a contract memory lacks (USE_CONTRACTS_V2; the old store loads every contract anyway): gathered with the
     * others asked meanwhile into ONE prefetch, each number once a session. onFetched listeners are told when a read
     * is in; a failed read is not told (no loop) and its numbers may be asked again later.
     */
    want(contractNo) {
        if (!USE_CONTRACTS_V2) return;
        const key = contractKey(contractNo);
        if (!key || wantAsked.has(key) || this.peek(key)) return;
        wantAsked.add(key);
        wantQueue.add(key);
        if (wantTimer) return;
        wantTimer = setTimeout(async () => {
            const batch = [...wantQueue];
            wantQueue = new Set();
            wantTimer = null;
            if (await this.prefetch(batch)) wantListeners.forEach((fn) => fn());
            else batch.forEach((c) => wantAsked.delete(c));
        }, WANT_GATHER_MS);
    },

    /** fn() after each read of want()'s numbers; returns the unsubscribe. */
    onFetched(fn) {
        wantListeners.add(fn);
        return () => { wantListeners.delete(fn); };
    },

    /** The contact row of a contract when the store is in memory, else null (no I/O). */
    peek(contractNo) {
        if (!memoryIndex) return null;
        return memoryIndex.get(contractKey(contractNo)) || null;
    },

    /** The contact row of a contract (loads the store first). */
    async lookup(contractNo) {
        if (USE_CONTRACTS_V2) { if (!this.peek(contractNo)) await this.prefetch([contractNo]); return this.peek(contractNo); }
        await this.ensureLoaded();
        return this.peek(contractNo);
    },

    /**
     * Contracts of a phone number, newest pick-up first, from the store in memory
     * (no I/O; returns [] until loaded). Matches on the last 9 digits, so a number
     * typed with or without its country code resolves the same customer.
     */
    contractsByPhone(rawPhone) {
        if (!memoryPhoneIndex) return [];
        return memoryPhoneIndex.get(phoneKey(rawPhone)) || [];
    },

    /** All contract rows of an identity number, newest pick-up first (no I/O; [] until loaded). */
    contractsByIdentity(identity) {
        if (!memoryIdentityIndex) return [];
        return memoryIdentityIndex.get(identityKey(identity)) || [];
    },

    /**
     * Customers matching a phone number, an identity number or a name, across the whole store
     * (every branch, open and closed contracts), newest activity first. No I/O ([] until loaded).
     * A passport (N1234567 - passportKey) is looked up as an identity.
     * Rows are grouped per customer by THE rule (customerKeyOf: Ejar's customer number, else identity, else the phone of a no-ID contract, else
     * the contract alone), each group holding its WHOLE customer (customerRowsOf) - what the profile opens.
     * Returns [{ key, name, phone, identity, branch, count, open, latest, rows }].
     */
    findCustomers(term, { limit = 8, fuzzy = true } = {}) {
        if (!memoryIndex) return [];
        let approximate = false;          // true = no exact name match, these are the closest names
        const raw = String(term || '').trim();
        const digits = raw.replace(/\D/g, '');
        const passport = passportKey(raw);
        let rows = [];
        if (/^[\d\s+\-()]+$/.test(raw)) {
            if (digits.length < 7) return [];
            const byIdentity = digits.length >= 10 ? this.contractsByIdentity(digits) : [];
            rows = [...byIdentity, ...this.contractsByPhone(digits)];
        } else if (passport) {
            rows = this.contractsByIdentity(passport);          // a passport is an identity (owner, 28 Sep 2026)
        } else {
            const tokens = nameKey(raw).split(' ').filter(Boolean);
            if (tokens.join('').length < 3) return [];
            for (const row of memoryIndex.values()) {
                const n = nameKey(row.n);
                if (n && tokens.every((t) => n.includes(t))) rows.push(row);
            }
            rows.sort((a, b) => dateSortKey(b.d) - dateSortKey(a.d));
            // nothing as typed: the closest names (a letter or two off), closest first
            if (!rows.length && fuzzy) {
                const byName = new Map();          // each distinct name is measured once
                const scored = [];
                for (const row of memoryIndex.values()) {
                    if (!row.n) continue;
                    let d = byName.get(row.n);
                    if (d === undefined) { d = nameDistance(tokens, row.n); byName.set(row.n, d); }
                    if (d !== null) scored.push([d, row]);
                }
                scored.sort((a, b) => (a[0] - b[0]) || (dateSortKey(b[1].d) - dateSortKey(a[1].d)));
                rows = scored.map(([, row]) => row);
                approximate = rows.length > 0;
            }
        }
        const groups = new Map();
        for (const row of rows) {
            if (!row || !row.c) continue;
            const key = customerKeyOf(row);
            if (groups.has(key)) continue;
            // the whole customer, not only the contracts this search hit (a name spelt otherwise on an older contract)
            const whole = customerRowsOf(key);
            groups.set(key, { key, rows: whole.length ? whole : [row] });
        }
        return [...groups.values()]
            .map(({ key, rows: list }) => {
                list.sort((a, b) => dateSortKey(b.d) - dateSortKey(a.d));
                const pick = (k) => (list.find((r) => r[k]) || {})[k] || '';
                return {
                    key,
                    name: pick('n'),
                    phone: pick('p'),
                    identity: pick('i'),
                    branch: list[0].b || '',
                    count: list.length,
                    open: list.some((r) => isOpenStatus(r.s)),
                    latest: list[0],
                    rows: list,
                    approximate,
                };
            })
            // exact: newest first; approximate: keep the closest-name order
            .sort((a, b) => (approximate ? 0 : dateSortKey(b.latest.d) - dateSortKey(a.latest.d)))
            .slice(0, limit);
    },

    /**
     * Customer profile: every contract row of one customer, resolved from a seed (identity, a known contract
     * number, or - with no contract - a phone), newest pick-up first, by THE rule of Ask Yelo's list (customerKeyOf):
     * the identity first (a customer with an ID is never linked by phone - the number can change); a contract with
     * no ID on file takes the other no-ID contracts of its phone; with neither, the contract alone. Returns how it
     * matched so the UI can say so. All in-memory, so opening the panel costs no Firestore reads.
     * Ejar's customer number comes first (customerKeyOf - owner, 4 Oct 2026): 'customer' = every contract of it.
     * Returns { by: 'customer'|'identity'|'phone'|'current'|'none', key, rows } - 'current' = the clicked contract only (no ID
     * on file), the word the profile shows and customerProfileRows uses (owner, 28 Sep 2026: this said 'contract').
     */
    customerContracts({ identity, contractNo, phone, customer } = {}) {
        if (!memoryIndex) return { by: 'none', key: '', rows: [] };
        const seedRow = contractNo ? (memoryIndex.get(contractKey(contractNo)) || null) : null;
        const idKey = identityKey(identity) || identityKey(seedRow && seedRow.i);
        // Ejar's customer number first (customerKeyOf): every contract of the customer, whatever ID each one shows
        const u = ejarCustomerKey(seedRow && seedRow.u) || ejarCustomerKey(customer) || customerOfIdentity(idKey);
        if (u) {
            const rows = customerRowsOf(`u:${u}`);
            if (rows.length) return { by: 'customer', key: u, rows };
        }
        if (idKey.length >= 4) {
            const rows = customerRowsOf(`i:${idKey}`);
            if (rows.length) return { by: 'identity', key: idKey, rows };
        } else {
            // no identity on file: the clicked contract's phone (with no contract, the phone given)
            const key = seedRow ? customerKeyOf(seedRow) : (!contractNo && noIdPhoneOf(phone) ? `p:${noIdPhoneOf(phone)}` : '');
            if (key.startsWith('p:')) {
                const rows = customerRowsOf(key);
                // "linked by phone" only when the phone links ANOTHER contract: the phone's rows always hold the clicked
                // one, so a lone no-ID contract said "linked by phone" and never "current rental only" (review, 28 Sep 2026)
                if (rows.length && (!seedRow || rows.some((r) => r.c !== seedRow.c))) return { by: 'phone', key: key.slice(2), rows };
            }
        }
        // Nothing to link by: just the current contract (the modal falls back to the clicked row when the contract isn't
        // in the store yet) - 'current' ("no ID on file") only when it has no ID; one with an ID is its identity's
        return {
            by: seedRow ? (idKey.length >= 4 ? 'identity' : 'current') : 'none',
            key: contractKey(contractNo || ''),
            rows: seedRow ? [seedRow] : [],
        };
    },

    /** Phone digits of a contract, or '' (loads the store first). */
    async phoneOf(contractNo) {
        const row = await this.lookup(contractNo);
        return row ? row.p : '';
    },

    /** Map contract -> row of the whole store (loads it first). */
    async index() {
        await this.load();
        return memoryIndex || new Map();
    },

    count: () => (memoryIndex ? memoryIndex.size : 0),

    clearCache() {
        if (USE_CONTRACTS_V2) {
            // the rows already read stay (a page's closed rows keep their phone / identity); the open list is read again
            storeOpenAt = 0;
            return Promise.resolve();
        }
        memoryChunks = null;
        memoryIndex = null;
        memoryPhoneIndex = null;
        return jsonCacheService.clearSpecificCache(CACHE_FILE);
    }
};

export default contactsService;
